import React from 'react';
import {
    Document,
    Image,
    Page,
    StyleSheet,
    Text,
    View,
} from '@react-pdf/renderer';
import logo from '../logo.png';
import { formatCpf } from './InputCpf';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from './TableComponents';

const { DateTime } = require('luxon');

const tableColumns = {
    descricao: { width: '45%', align: 'left' },
    pedido: { width: '15%', align: 'center' },
    dataCompra: { width: '20%', align: 'center' },
    quantidade: { width: '20%', align: 'center' },
};

// Create styles
const styles = StyleSheet.create({
    page: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 32,
        width: '100%',
        fontSize: 14,
    },
    logo: {
        width: 64,
        height: 48,
        alignSelf: 'center',
        marginBottom: 30,
    },
    titulo: {
        marginTop: 20,
        alignSelf: 'center',
        fontSize: 16,
        fontWeight: 'normal',
    },
    tituloBold: {
        alignSelf: 'center',
        fontSize: 16,
        fontWeight: 'ultrabold',
    },
    itemSection: {
        display: 'flex',
        flexDirection: 'column',
        width: 500,
    },
    itemTotal: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    totalText: {
        fontStyle: 'bold',
        fontWeight: 'heavy',
        paddingTop: 22,
        fontSize: 12,
        width: '85%',
    },
    totalNumber: {
        paddingTop: 22,
        fontWeight: 800,
        fontSize: 12,
        width: '15%',
    },
    assinaturaSection: {
        marginTop: 100,
        alignSelf: 'center',
        alignItems: 'center',
    },
    assinatura: {
        borderBottom: '1 solid #000',
        width: 250,
        margin: '4 auto',
    },
    cpfTexto: {
        marginTop: 4,
        marginLeft: 2,
        fontSize: 10,
    },
    nomeTexto: {
        fontSize: 13,
        fontWeight: 'thin',
    },
    cpf: {
        fontSize: 12,
    },
    nome: {
        fontWeight: 500,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignSelf: 'center',
        fontSize: 12,
    },
});

const Total = ({ value, getQuantidade = ({ quantidade }) => quantidade }) => {
    const items = value.reduce(
        (list, { listaPrecoItemNomeKit, nomeItem, ...rest }) => {
            const nome = listaPrecoItemNomeKit || nomeItem;
            let item = list.find((e) => e.nomeItem === nome);
            if (item) item.quantidade += getQuantidade(rest);
            else list.push({ nomeItem: nome, quantidade: getQuantidade(rest) });
            return list;
        },
        []
    );

    const total = items.reduce(
        (total, { quantidade }) => total + quantidade,
        0
    );
    return (
        <View style={styles.itemSection}>
            <View style={styles.itemTotal}>
                <Text style={styles.totalText}>TOTAL DE CARTELAS</Text>
                <Text style={styles.totalNumber}>{total}</Text>
            </View>
        </View>
    );
};

const MyDocument = ({
    assinante: { cpf, nome } = {},
    compartilhadoComigo = [],
    cartelas = [],
    selos = [],
    usuario = {},
}) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <Image src={logo} style={styles.logo} />
            <Text style={styles.tituloBold}>Comprovante de Entrega</Text>
            <Text style={styles.nomeTexto}>Nome: {nome}</Text>
            <Text style={styles.cpfTexto}>Cpf: {formatCpf(cpf)}</Text>
            {!!cartelas.length && (
                <>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell {...tableColumns.descricao}>
                                    Cartelas
                                </TableCell>
                                <TableCell {...tableColumns.pedido}>
                                    Pedido
                                </TableCell>
                                <TableCell {...tableColumns.dataCompra}>
                                    Data da Compra
                                </TableCell>
                                <TableCell {...tableColumns.quantidade}>
                                    Quantidade
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cartelas.map((cartela, index) => (
                                <TableRow key={cartela.id || index}>
                                    <TableCell {...tableColumns.descricao}>
                                        {cartela.listaPrecoItemNomeKit ||
                                            cartela.nomeItem}
                                    </TableCell>
                                    <TableCell {...tableColumns.pedido}>
                                        {cartela.pedido}
                                    </TableCell>
                                    <TableCell {...tableColumns.dataCompra}>
                                        {DateTime.fromISO(
                                            cartela.dataPedido
                                        ).toFormat('dd/MM/yyyy')}
                                    </TableCell>
                                    <TableCell {...tableColumns.quantidade}>
                                        {cartela.quantidade}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Total title="Cartelas" value={cartelas} />
                </>
            )}
            {!!compartilhadoComigo.length && (
                <>
                    <Text style={styles.titulo}>Cartelas Compartilhadas</Text>
                    {compartilhadoComigo.map((compartilhadoPor, index) => (
                        <React.Fragment
                            key={compartilhadoPor.perfil?.cpf || index}
                        >
                            <Text style={styles.nomeTexto}>
                                {compartilhadoPor.perfil.nome}
                            </Text>
                            <Text style={styles.cpfTexto}>
                                {formatCpf(compartilhadoPor.perfil.cpf)}
                            </Text>
                        </React.Fragment>
                    ))}
                </>
            )}
            {!!selos.length && (
                <TableBody>
                    {selos.map((selo, index) => (
                        <TableRow key={selo.id || index}>
                            <TableCell {...tableColumns.descricao}>
                                {selo.listaPrecoItemNomeKit}
                            </TableCell>
                            <TableCell {...tableColumns.pedido}>
                                {selo.pedido}
                            </TableCell>
                            <TableCell {...tableColumns.dataCompra}>
                                {DateTime.fromISO(selo.dataPedido).toFormat(
                                    'dd/MM/yyyy'
                                )}
                            </TableCell>
                            <TableCell {...tableColumns.quantidade}>
                                {selo.quantidade}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            )}
        </Page>
    </Document>
);

// Display Name para debug
MyDocument.displayName = 'MyDocument';

export default MyDocument;
